import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Printer, Award, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { treinamentoCursoService } from '@/services/treinamentosApi';
import { TreinamentoCurso } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

export default function Certificado() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [curso, setCurso] = useState<TreinamentoCurso | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    treinamentoCursoService
      .getById(id)
      .then(setCurso)
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!curso?.certificado) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <Award className="h-10 w-10 text-muted-foreground" />
        <p className="text-muted-foreground">Certificado ainda não disponível. Conclua todas as aulas do curso.</p>
        <Button variant="outline" onClick={() => navigate(`/treinamentos/curso/${id}`)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Voltar ao curso
        </Button>
      </div>
    );
  }

  const data = new Date(curso.certificado.emitido_em).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-muted/40 p-6">
      {/* Barra de ações — escondida na impressão */}
      <div className="mx-auto mb-6 flex max-w-4xl items-center justify-between print:hidden">
        <Button variant="ghost" onClick={() => navigate(`/treinamentos/curso/${id}`)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Voltar ao curso
        </Button>
        <Button onClick={() => window.print()}>
          <Printer className="mr-2 h-4 w-4" /> Baixar PDF / Imprimir
        </Button>
      </div>

      {/* Certificado */}
      <div className="mx-auto max-w-4xl bg-white shadow-lg print:shadow-none">
        <div className="m-4 border-[3px] border-double border-amber-500/70 p-10 text-center print:m-0">
          <div className="mb-6 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <Award className="h-9 w-9" />
            </div>
          </div>
          <p className="text-sm uppercase tracking-[0.3em] text-amber-600">Certificado de Conclusão</p>
          <p className="mt-8 text-base text-gray-600">Certificamos que</p>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">{user?.nome}</h1>
          <p className="mt-6 text-base text-gray-600">concluiu com êxito o curso</p>
          <h2 className="mt-2 text-2xl font-semibold text-gray-800">{curso.titulo}</h2>

          <div className="mt-12 flex items-end justify-between text-sm text-gray-600">
            <div className="text-left">
              <div className="border-t border-gray-400 px-8 pt-1">Data</div>
              <div className="px-8 text-gray-800">{data}</div>
            </div>
            <div className="text-right">
              <div className="border-t border-gray-400 px-8 pt-1">Código de validação</div>
              <div className="px-8 font-mono text-gray-800">{curso.certificado.codigo}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
